// src/pages/VerifyEmail.jsx
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  MailCheck,
} from "lucide-react";
import { toast } from "react-hot-toast";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

export default function VerifyEmail() {
  const [status, setStatus] = useState("Verifying...");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState("");

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token")?.trim() || "";
  const type = (searchParams.get("type") || "signup").trim().toLowerCase();

  const redirectedStatus = searchParams.get("status") || null;
  const redirectedNewEmail = searchParams.get("newEmail") || null;
  const redirectedOldEmail = searchParams.get("oldEmail") || null;

  const broadcast = (payload) => {
    try {
      if (typeof BroadcastChannel !== "undefined") {
        const bc = new BroadcastChannel("email-verification");
        bc.postMessage(payload);
        bc.close();
      } else {
        localStorage.setItem(
          "emailVerificationCompleted",
          JSON.stringify(payload)
        );
      }
    } catch (e) {
      try {
        localStorage.setItem(
          "emailVerificationCompleted",
          JSON.stringify(payload)
        );
      } catch (err) {
        console.warn("Could not broadcast verification:", err);
      }
    }
  };

  useEffect(() => {
    const verify = async () => {
      if (!token && redirectedStatus) {
        if (redirectedStatus === "success") {
          setStatus("✅ Verified successfully!");
          setSuccess(true);

          const storedUser = JSON.parse(localStorage.getItem("user")) || null;
          if (storedUser) {
            const newEmail = redirectedNewEmail || storedUser.email;
            localStorage.setItem(
              "user",
              JSON.stringify({ ...storedUser, email: newEmail })
            );
          }

          broadcast({
            at: Date.now(),
            newEmail: redirectedNewEmail || null,
            oldEmail: redirectedOldEmail || null,
            source: "redirect",
          });

          setLoading(false);
          setTimeout(() => {
            if (type === "signup") navigate("/login");
            else if (["email-change", "admin-email-change"].includes(type))
              navigate("/dashboard/profile");
            else navigate("/login");
          }, 2000);
          return;
        } else {
          setStatus("❌ Verification failed or expired.");
          setSuccess(false);
          setLoading(false);
          setTimeout(() => navigate("/dashboard/profile"), 1500);
          return;
        }
      }

      if (!token) {
        setStatus("❌ Invalid or missing verification link.");
        setSuccess(false);
        setLoading(false);
        setTimeout(() => {
          if (type === "signup") navigate("/login");
          else if (["email-change", "admin-email-change"].includes(type))
            navigate("/dashboard/profile");
          else navigate("/login");
        }, 2500);
        return;
      }

      try {
        let endpoint = "";
        if (type === "email-change") {
          endpoint = `${API_BASE}/api/user/profile/verify-email?token=${token}`;
        } else if (type === "admin-email-change") {
          endpoint = `${API_BASE}/api/admin/profile/verify-new-email?token=${token}`;
        } else {
          endpoint = `${API_BASE}/api/auth/verify-email?token=${token}`;
        }

        const res = await axios.get(endpoint);
        setStatus(res.data.message || "✅ Verified successfully!");
        setSuccess(true);

        if (type === "email-change") {
          const storedUser = JSON.parse(localStorage.getItem("user")) || null;
          if (storedUser) {
            const newEmail = res.data.newEmail || storedUser.email;
            localStorage.setItem(
              "user",
              JSON.stringify({ ...storedUser, email: newEmail })
            );
          }
          broadcast({
            at: Date.now(),
            newEmail: res.data.newEmail || null,
            oldEmail: res.data.oldEmail || null,
            source: "api",
          });
        }
      } catch (err) {
        console.error("Verification error:", err);
        setStatus(
          err.response?.data?.message ||
            err.response?.data?.error ||
            "❌ Verification failed."
        );
        setSuccess(false);
      } finally {
        setLoading(false);
        setTimeout(() => {
          if (type === "signup") navigate("/login");
          else if (["email-change", "admin-email-change"].includes(type))
            navigate("/dashboard/profile");
          else navigate("/login");
        }, 2500);
      }
    };

    verify();
  }, [
    token,
    redirectedStatus,
    redirectedNewEmail,
    redirectedOldEmail,
    type,
    navigate,
  ]);

  const handleResend = async () => {
    try {
      setResendLoading(true);
      setResendMessage("");

      let endpoint = "";
      let payload = {};
      let config = {};

      if (type === "signup") {
        endpoint = `${API_BASE}/api/auth/resend-verification`;
        payload = { email: searchParams.get("email") || "" };
      } else if (type === "email-change") {
        endpoint = `${API_BASE}/api/user/profile/resend-verification`;
        config = {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(localStorage.getItem("user"))?.token
            }`,
          },
        };
      } else if (type === "admin-email-change") {
        endpoint = `${API_BASE}/api/admin/profile/resend-verification`;
        config = {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(localStorage.getItem("admin"))?.token
            }`,
          },
        };
      }

      const res = await axios.post(endpoint, payload, config);
      setResendMessage(res.data.message || "✅ Verification email sent.");
      toast.success("Verification email resent!");
    } catch (err) {
      console.error("Resend error:", err);
      setResendMessage(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "❌ Failed to resend verification email."
      );
      toast.error("Failed to resend email");
    } finally {
      setResendLoading(false);
    }
  };

  // === UI ===
  return (
    <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-blue-100 via-indigo-100 to-purple-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-700 p-4 transition-all duration-700">
      <div
        className={`relative p-10 rounded-3xl shadow-2xl max-w-md w-full text-center backdrop-blur-xl transition-all duration-500 ${
          success
            ? "bg-white/80 dark:bg-gray-900/70 border-2 border-green-400"
            : "bg-white/80 dark:bg-gray-900/70 border-2 border-red-400"
        }`}
      >
        {/* Decorative accent */}
        <div
          className={`absolute inset-x-0 top-0 h-1.5 rounded-t-3xl ${
            success ? "bg-green-500" : "bg-red-500"
          }`}
        ></div>

        <div className="mb-6 mt-4 flex justify-center">
          {loading ? (
            <Loader2 className="w-14 h-14 animate-spin text-blue-600" />
          ) : success ? (
            <CheckCircle className="w-16 h-16 text-green-500 animate-pulse" />
          ) : (
            <XCircle className="w-16 h-16 text-red-500 animate-pulse" />
          )}
        </div>

        <h2 className="text-3xl font-extrabold mb-3 text-gray-800 dark:text-gray-100">
          {loading ? "Verifying Email..." : "Email Verification"}
        </h2>
        <p
          className={`text-lg mb-6 ${
            success
              ? "text-green-700 dark:text-green-300"
              : "text-gray-700 dark:text-gray-300"
          }`}
        >
          {status}
        </p>

        {!loading && !success && (
          <>
            <p className="mt-2 text-sm italic text-gray-600 dark:text-gray-400">
              Please check your link or contact support if the issue persists.
            </p>

            <button
              onClick={handleResend}
              disabled={resendLoading}
              className="mt-6 flex items-center justify-center w-full bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-gray-900 py-3 rounded-xl font-semibold transition duration-300 disabled:opacity-50"
            >
              {resendLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" /> Resend Verification Email
                </>
              )}
            </button>
            {resendMessage && (
              <p className="mt-3 text-sm text-blue-700 dark:text-blue-400">
                {resendMessage}
              </p>
            )}
          </>
        )}

        {!loading && success && (
          <div className="flex flex-col items-center">
            <p className="mt-2 text-sm italic text-gray-600 dark:text-gray-400">
              Redirecting you to{" "}
              <span className="font-medium text-blue-700 dark:text-yellow-400">
                {type === "signup" ? "Login" : "Profile"}
              </span>{" "}
              shortly...
            </p>
            <MailCheck className="w-8 h-8 mt-3 text-blue-500 animate-bounce" />
          </div>
        )}

        <p className="text-xs mt-10 text-gray-400 dark:text-gray-500">
          © 2025 UPSC Journey. All rights reserved.
        </p>
      </div>
    </div>
  );
}
